/**
 * Shared CSS styles for MCP UI views.
 *
 * All styles are self-contained — no external dependencies.
 * Designed for rendering inside sandboxed iframes (mcp-ui).
 */
export const BASE_STYLES = `
  :root {
    /* Surface colors */
    --bg-primary: #0d1117;
    --bg-secondary: #161b22;
    --bg-tertiary: #1c2129;
    --bg-card: #161b22;
    --bg-card-hover: #1c2129;
    --bg-inset: #0d1117;

    /* Border colors */
    --border-primary: #30363d;
    --border-secondary: #21262d;
    --border-subtle: rgba(240, 246, 252, 0.06);

    /* Text colors */
    --text-primary: #e6edf3;
    --text-secondary: #8b949e;
    --text-tertiary: #6e7681;
    --text-link: #58a6ff;
    --text-inverse: #0d1117;

    /* Accent colors */
    --accent-blue: #58a6ff;
    --accent-green: #3fb950;
    --accent-orange: #d29922;
    --accent-red: #f85149;
    --accent-purple: #bc8cff;
    --accent-cyan: #39d2c0;
    --accent-pink: #f778ba;

    /* Badge colors */
    --badge-repo-bg: rgba(88, 166, 255, 0.15);
    --badge-repo-text: #58a6ff;
    --badge-repo-border: rgba(88, 166, 255, 0.3);
    --badge-file-bg: rgba(188, 140, 255, 0.15);
    --badge-file-text: #bc8cff;
    --badge-file-border: rgba(188, 140, 255, 0.3);
    --badge-web-bg: rgba(57, 210, 192, 0.15);
    --badge-web-text: #39d2c0;
    --badge-web-border: rgba(57, 210, 192, 0.3);

    /* Status colors */
    --status-ready: #3fb950;
    --status-indexing: #d29922;
    --status-error: #f85149;
    --status-unknown: #6e7681;

    /* Score colors */
    --score-high: #3fb950;
    --score-medium: #d29922;
    --score-low: #f85149;

    /* Confidence colors */
    --confidence-high-bg: rgba(63, 185, 80, 0.15);
    --confidence-high-text: #3fb950;
    --confidence-medium-bg: rgba(210, 153, 34, 0.15);
    --confidence-medium-text: #d29922;
    --confidence-low-bg: rgba(248, 81, 73, 0.15);
    --confidence-low-text: #f85149;

    /* Spacing */
    --space-xs: 4px;
    --space-sm: 8px;
    --space-md: 12px;
    --space-lg: 16px;
    --space-xl: 24px;
    --space-2xl: 32px;

    /* Typography */
    --font-sans: -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans", Helvetica, Arial, sans-serif;
    --font-mono: "SF Mono", "Cascadia Code", "Fira Code", "JetBrains Mono", ui-monospace, SFMono-Regular, "Liberation Mono", Menlo, Monaco, Consolas, monospace;
    --font-size-xs: 11px;
    --font-size-sm: 12px;
    --font-size-md: 14px;
    --font-size-lg: 16px;
    --font-size-xl: 20px;
    --font-size-2xl: 24px;

    /* Radius */
    --radius-sm: 4px;
    --radius-md: 6px;
    --radius-lg: 8px;
    --radius-xl: 12px;

    /* Shadows */
    --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.3);
    --shadow-md: 0 2px 8px rgba(0, 0, 0, 0.3);
    --shadow-lg: 0 4px 16px rgba(0, 0, 0, 0.4);

    /* Transitions */
    --transition-fast: 120ms ease;
    --transition-normal: 200ms ease;
  }

  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  body {
    font-family: var(--font-sans);
    font-size: var(--font-size-md);
    color: var(--text-primary);
    background: var(--bg-primary);
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* Container */
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: var(--space-lg);
  }

  /* Header */
  .header {
    margin-bottom: var(--space-xl);
  }

  .header-title {
    font-size: var(--font-size-xl);
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: var(--space-xs);
    display: flex;
    align-items: center;
    gap: var(--space-sm);
  }

  .header-subtitle {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
  }

  /* Badges */
  .badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 9999px;
    font-size: var(--font-size-xs);
    font-weight: 500;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    border: 1px solid;
    white-space: nowrap;
  }

  .badge-repo {
    background: var(--badge-repo-bg);
    color: var(--badge-repo-text);
    border-color: var(--badge-repo-border);
  }

  .badge-file {
    background: var(--badge-file-bg);
    color: var(--badge-file-text);
    border-color: var(--badge-file-border);
  }

  .badge-web {
    background: var(--badge-web-bg);
    color: var(--badge-web-text);
    border-color: var(--badge-web-border);
  }

  /* Status dot */
  .status-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .status-ready { background: var(--status-ready); box-shadow: 0 0 6px var(--status-ready); }
  .status-indexing { background: var(--status-indexing); box-shadow: 0 0 6px var(--status-indexing); animation: pulse 2s infinite; }
  .status-error { background: var(--status-error); box-shadow: 0 0 6px var(--status-error); }
  .status-unknown { background: var(--status-unknown); }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }

  /* Tags */
  .tag {
    display: inline-flex;
    align-items: center;
    padding: 1px 6px;
    border-radius: var(--radius-sm);
    font-size: var(--font-size-xs);
    color: var(--text-secondary);
    background: var(--bg-tertiary);
    border: 1px solid var(--border-secondary);
  }

  /* Metadata row */
  .meta-row {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    flex-wrap: wrap;
  }

  .meta-separator {
    width: 1px;
    height: 12px;
    background: var(--border-primary);
    flex-shrink: 0;
  }

  /* Monospace text */
  .mono {
    font-family: var(--font-mono);
    font-size: var(--font-size-sm);
  }

  /* Code block */
  .code-block {
    background: var(--bg-inset);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-md);
    padding: var(--space-md);
    overflow-x: auto;
    font-family: var(--font-mono);
    font-size: var(--font-size-sm);
    line-height: 1.6;
    color: var(--text-primary);
    white-space: pre;
    tab-size: 2;
  }

  /* Details/Summary (expand/collapse) */
  details {
    border-top: 1px solid var(--border-secondary);
    margin-top: var(--space-md);
  }

  details summary {
    cursor: pointer;
    padding: var(--space-sm) 0;
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    user-select: none;
    list-style: none;
    display: flex;
    align-items: center;
    gap: var(--space-sm);
  }

  details summary::-webkit-details-marker {
    display: none;
  }

  details summary::before {
    content: "\\25B6";
    font-size: 10px;
    color: var(--text-tertiary);
    transition: transform var(--transition-fast);
    display: inline-block;
  }

  details[open] summary::before {
    transform: rotate(90deg);
  }

  details summary:hover {
    color: var(--text-primary);
  }

  details .details-content {
    padding: var(--space-md) 0;
  }

  /* Truncate text */
  .truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Empty state */
  .empty-state {
    text-align: center;
    padding: var(--space-2xl) var(--space-lg);
    color: var(--text-secondary);
  }

  .empty-state-title {
    font-size: var(--font-size-lg);
    font-weight: 500;
    color: var(--text-primary);
    margin-bottom: var(--space-sm);
  }

  /* Score bar */
  .score-bar-container {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    min-width: 120px;
  }

  .score-bar-track {
    flex: 1;
    height: 4px;
    background: var(--bg-inset);
    border-radius: 2px;
    overflow: hidden;
  }

  .score-bar-fill {
    height: 100%;
    border-radius: 2px;
    transition: width var(--transition-normal);
  }

  .score-label {
    font-size: var(--font-size-xs);
    font-family: var(--font-mono);
    min-width: 36px;
    text-align: right;
  }

  /* Pill list */
  .pill-list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-xs);
    list-style: none;
  }

  .pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: var(--radius-sm);
    font-size: var(--font-size-xs);
    font-family: var(--font-mono);
    color: var(--text-secondary);
    background: var(--bg-tertiary);
    border: 1px solid var(--border-secondary);
  }

  /* Section heading inside cards */
  .section-label {
    font-size: var(--font-size-xs);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-tertiary);
    margin-bottom: var(--space-xs);
  }
`;
